import { z } from 'zod';

export const userSchema = z.object({
  id: z.number().int().positive(),
  telegramId: z.number().int(),
  username: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  languageCode: z.string().nullable(),
  balanceCredits: z.number().int().nonnegative(),
  niche: z.string().nullable(),
  product: z.string().nullable(),
  targetAudience: z.string().nullable(),
  onboardedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type User = z.infer<typeof userSchema>;

/** Публичный DTO юзера для TMA (без системных полей). */
export const userPublicSchema = userSchema
  .pick({
    id: true,
    telegramId: true,
    username: true,
    firstName: true,
    lastName: true,
    languageCode: true,
    balanceCredits: true,
    niche: true,
    product: true,
    targetAudience: true,
    onboardedAt: true,
  })
  .extend({
    isAdmin: z.boolean(),
    // Credits of the lot(s) that burn first, and when (Iter 19). Null when the balance is 0.
    creditsNextExpiry: z
      .object({ credits: z.number().int().positive(), expiresAt: z.string() })
      .nullable(),
    // End of turbo (00:00 Almaty after the last day); null when turbo is off.
    turboUntil: z.string().nullable(),
    // Iter 21: the phone itself never goes back to the TMA — only these flags.
    hasPhone: z.boolean(),
    consentRequired: z.boolean(),
  });

export type UserPublic = z.infer<typeof userPublicSchema>;

/** Онбординг-форма: ниша / продукт / ЦА. */
export const onboardingInputSchema = z.object({
  niche: z.string().min(1).max(500),
  product: z.string().min(1).max(500),
  targetAudience: z.string().min(1).max(500),
});

export type OnboardingInput = z.infer<typeof onboardingInputSchema>;

/**
 * Частичное обновление профиля (онбординг и будущая страница «Профиль»).
 * Каждое поле — опциональное; если присутствует, должно быть строкой 1–500 после trim().
 * Сервер сам выставит onboarded_at = now(), если после апдейта все три поля непустые
 * и onboarded_at IS NULL.
 */
export const profileUpdateSchema = z
  .object({
    niche: z.string().trim().min(1).max(500).optional(),
    product: z.string().trim().min(1).max(500).optional(),
    targetAudience: z.string().trim().min(1).max(500).optional(),
  })
  .strict();

export type ProfileUpdateInput = z.infer<typeof profileUpdateSchema>;

/**
 * Ответ GET /profile и PATCH /profile.
 */
export const profileResponseSchema = z.object({
  user: userPublicSchema,
});

export type ProfileResponse = z.infer<typeof profileResponseSchema>;
